import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PhoneCall, PhoneIncoming, PhoneMissed, CalendarPlus, ListChecks, Timer, Activity, ArrowRight, AlertTriangle, Inbox, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Kpi, Panel, Seg, EmptyState, ErrorState, Dot, Skel, fmtDur, whenParts } from "@/components/shally/ui";
import { CallList } from "@/components/shally/CallList";
import { CallDetail } from "@/components/shally/CallDetail";
import { ActivityChart, ChartLegend } from "@/components/shally/ActivityChart";

const RANGES = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"]];
const calls = (params) => api.get("/call-intel/calls", { params }).then((r) => r.data);

function Kpis({ st, loading }) {
  const rate = st?.total ? Math.round((st.answered / st.total) * 100) : null;
  const avg = st?.answered ? Math.round(st.total_duration_s / st.answered) : 0;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="shally-kpis">
      <Kpi id="total" label="Total calls" icon={PhoneCall} value={st?.total ?? 0} loading={loading} />
      <Kpi id="answered" label="Answered" icon={PhoneIncoming} value={st?.answered ?? 0} hint={rate !== null ? `${rate}% answer rate` : ""} loading={loading} />
      <Kpi id="missed" label="Missed" icon={PhoneMissed} value={st?.missed_failed ?? 0} loading={loading} />
      <Kpi id="bookings" label="Booking requests" icon={CalendarPlus} value={st?.booking_requests ?? 0} hint="Pending leads" loading={loading} />
      <Kpi id="followups" label="Open follow-ups" icon={ListChecks} value={st?.follow_ups ?? 0} hint="From calls in this period" loading={loading} />
      <Kpi id="avg" label="Avg. call duration" icon={Timer} value={fmtDur(avg)} hint="Answered calls" loading={loading} />
    </div>);
}

function AttentionItem({ c, onOpen, tone, label }) {
  const [d, t] = whenParts(c.started_at);
  return (
    <button type="button" onClick={() => onOpen(c.id)} data-testid={`attn-${c.id}`}
      className="flex w-full items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40">
      <span className="mt-1"><Dot tone={tone} label="" /></span>
      <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-foreground">{c.customer || c.caller || "Unknown caller"}</span>
        <span className="line-clamp-1 text-xs text-muted-foreground">{c.headline || label}</span></span>
      <span className="shrink-0 text-right text-[11px] text-muted-foreground">{label}<br />{d} {t}</span>
    </button>);
}

function NeedsAttention({ onOpen }) {
  const urgent = useQuery({ queryKey: ["ci-calls", "attn-urgent"], queryFn: () => calls({ range: "", emergency: true, follow_up: "yes", page_size: 5 }) });
  const fu = useQuery({ queryKey: ["ci-calls", "attn-fu"], queryFn: () => calls({ range: "", follow_up: "yes", page_size: 6 }) });
  const err = urgent.error || fu.error;
  const ids = new Set((urgent.data?.items || []).map((c) => c.id));
  const rest = (fu.data?.items || []).filter((c) => !ids.has(c.id));
  const n = (fu.data?.total || 0);
  return (
    <Panel title="Needs attention" icon={AlertTriangle} testid="shally-attention" className="flex flex-col"
      action={<Link to="/shally/follow-ups" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="attn-all">View all{n ? ` (${n})` : ""}<ArrowRight className="h-3 w-3" /></Link>}>
      {err ? <ErrorState error={err} onRetry={() => { urgent.refetch(); fu.refetch(); }} />
        : urgent.isLoading || fu.isLoading ? <div className="space-y-3">{[0, 1, 2].map((i) => <Skel key={i} className="h-9" />)}</div>
          : !ids.size && !rest.length ? <EmptyState icon={CheckCircle2} title="All caught up" text="No open follow-ups or urgent calls." testid="attn-empty" />
            : <div className="-mx-2 space-y-0.5">
              {(urgent.data?.items || []).map((c) => <AttentionItem key={c.id} c={c} onOpen={onOpen} tone="red" label="Urgent" />)}
              {rest.slice(0, 6 - ids.size).map((c) => <AttentionItem key={c.id} c={c} onOpen={onOpen} tone="amber" label="Callback" />)}
            </div>}
    </Panel>);
}

export default function ShallyOverview() {
  const [range, setRange] = useState("30d"), [open, setOpen] = useState(null);
  const { data, error, isLoading, refetch } = useQuery({ queryKey: ["ci-calls", "overview", range], queryFn: () => calls({ range, page_size: 6 }) });
  return (
    <div className="space-y-6" data-testid="shally-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="text-base font-bold text-foreground md:text-lg">Overview</h2><p className="text-sm text-muted-foreground">How Shally handled your calls in the last {range.replace("d", "")} days.</p></div>
        <Seg value={range} onChange={setRange} opts={RANGES} testid="ov-range" />
      </div>
      {error ? <ErrorState error={error} onRetry={refetch} /> : <Kpis st={data?.stats} loading={isLoading} />}
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Call activity" icon={Activity} className="lg:col-span-2" testid="shally-activity" action={<ChartLegend />}><ActivityChart range={range} /></Panel>
        <NeedsAttention onOpen={setOpen} />
      </div>
      <Panel title="Recent calls" icon={PhoneCall} flush testid="shally-recent"
        action={<Link to="/shally/calls" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="recent-all">All calls<ArrowRight className="h-3 w-3" /></Link>}>
        <CallList items={data?.items} loading={isLoading} onOpen={setOpen} testid="recent-table"
          empty={<EmptyState icon={Inbox} title="No calls yet" text="When Shally answers calls for your business, they'll appear here." testid="recent-empty" />} />
      </Panel>
      <CallDetail cid={open} onClose={() => setOpen(null)} />
    </div>);
}
